import { useRouter } from 'next/router';
import { useEffect } from 'react';

import NonF2fDiagnosisAppInstallBottomSheet from '@/components/templates/f2f/hospital/HospitalDetailPartnerTemplate/HospitalBottomSheet/NonF2fDiagnosisAppInstallBottomSheet/NonF2fDiagnosisAppInstallBottomSheet';
import NonF2fDiagnosisAppInstallQrCodeBottomSheet from '@/components/templates/f2f/hospital/HospitalDetailPartnerTemplate/HospitalBottomSheet/NonF2fDiagnosisAppInstallQrCodeBottomSheet/NonF2fDiagnosisAppInstallQrCodeBottomSheet';
import useAppDownloadCujModal from '@/hooks/Cuj/useAppDownloadCujModal';
import useSearchHospitalsGenerally from '@/hooks/hospital/list/useSearchHospitalsGenerally';
import useInfiniteSearchNonF2fDoctors from '@/hooks/search/useInfiniteSearchNonF2fDoctors';
import useModal from '@/hooks/useModal';
import { HospitalSearchCommonFilter, HospitalSortFilter } from '@/parsers/f2f/common';
import { Client } from '@/types/app';
import {
  DoctorSortType,
  DoctorType,
  MedicalCategoryType,
  TelemedicineEligibleTargetType,
} from '@/types/bff/gql-schema';
import { ROUTES } from '@/types/common';
import { HospitalSearchQueries } from '@/types/hospital';
import { MODAL_TYPE } from '@/types/modal';
import { NATIVE_POST_MESSAGE_TYPE, isWebView, sendPostMessageToNative } from '@/utils/mobile/RNPlugins';
import { addQueryString } from '@/utils/url';

import useHospitalSearchFilters from './useHospitalSearchFilters';
import useSearchHospitalsByKeyword from './useSearchHospitalsByKeyword';
import useUserLocation from './useUserLocation';

export interface EntireBottomSheetProps {
  isEntireFilterBottomSheet: boolean;
  locationName: string;
  hospitalCommonFilter: HospitalSearchCommonFilter;
  selectedSortFilter: HospitalSortFilter;
  sortFilterList: HospitalSortFilter[];
  switchRegionFilter: () => void;
  toggleSearchEntireFilterBottomSheet: () => void;
  isDefaultHospitalNearMode: () => boolean;
  isChangedHospitalFilters: () => boolean;
  updateEntireFilter: (filters: { sortFilter: HospitalSortFilter; commonFilter: HospitalSearchCommonFilter }) => void;
}

interface HospitalSearchViewModel
  extends ReturnType<typeof useSearchHospitalsByKeyword>,
    ReturnType<typeof useUserLocation>,
    ReturnType<typeof useHospitalSearchFilters>,
    ReturnType<typeof useSearchHospitalsGenerally> {
  getSearchEntireFilterBottomSheetProps: () => EntireBottomSheetProps;
  filterCount: number;
  goToEventPage: (eventUrl: string, hospitalPid: string) => void;
  clickNonF2fDoctorListBanner: () => void;
  matchedCategory: { category: MedicalCategoryType; id: string } | null;
}

const APP_INSTALL_LINK_FROM_HOSPITAL_SEARCH = 'https://abr.ge/fhcijh';
const useHospitalsSearchViewModel = (client: Client): HospitalSearchViewModel => {
  const router = useRouter();
  const queries = router.query as HospitalSearchQueries;
  const { setIsCujModalTrackingInfo } = useAppDownloadCujModal();

  const locationHook = useUserLocation();
  const hospitalFiltersHook = useHospitalSearchFilters();
  const keywordHook = useSearchHospitalsByKeyword();
  const { showModal, closeModal } = useModal();
  const { category: matchedCategory } = useInfiniteSearchNonF2fDoctors(
    {
      request: {
        keyword: keywordHook.keyword,
        sortType: DoctorSortType.Random,
        page: 1,
        limit: 1,
        doctorType: DoctorType.Doctor,
        telemedicineEligibleTargetType: TelemedicineEligibleTargetType.NightAndHolidayOnly,
      },
    },
    { enabled: !!keywordHook.keyword },
  );

  const { getCoordinateToString, regionByDepth, switchRegionFilter, getHeaderFullAddress, isLocationSelected } =
    locationHook;

  const {
    isDefaultHospitalNearMode,
    isSearchEntireFilterBottomSheet,
    toggleSearchEntireFilterBottomSheet,
    updateEntireFilter,
    hospitalCommonFilter,
    sortFilterList,
    selectedSortFilter,
    commonFilterCount,
    isChangedHospitalFilters,
  } = hospitalFiltersHook;

  const searchHospitalHook = useSearchHospitalsGenerally({
    ...getCoordinateToString(),
    keyword: queries.keyword,
    sortType: queries.sortType,
    regionByDepth:
      isDefaultHospitalNearMode() && !!regionByDepth
        ? { ...regionByDepth, siDoName: regionByDepth?.siDoName || '' }
        : undefined,
    commonFilter: queries.commonFilter,
  });

  const filterCount = commonFilterCount + (isLocationSelected() && isDefaultHospitalNearMode() ? 1 : 0);

  const goToEventPage = (eventUrl: string, hospitalPid: string) => {
    if (isWebView()) {
      sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_PAGE, {
        screenName: ROUTES.HOSPITAL.EVENT.appScreenName,
        params: { hospitalPid, eventUrl },
      });
      return;
    }
    router.push(
      `${ROUTES.HOSPITAL.EVENT.path}${addQueryString({
        eventUrl,
        hospitalPid,
      })}`,
    );
  };

  const clickNonF2fDoctorListBanner = () => {
    if (isWebView() && matchedCategory) {
      sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_DOCTOR_LIST_WITH_TARGET_CHECK, {
        type: matchedCategory.category,
        id: matchedCategory.id,
      });
      return;
    }
    if (client.isMobile) {
      return showModal({
        type: MODAL_TYPE.BASIC,
        props: {
          body: (
            <NonF2fDiagnosisAppInstallBottomSheet
              appInstallLink={APP_INSTALL_LINK_FROM_HOSPITAL_SEARCH}
              onClickOverlay={closeModal}
            />
          ),
        },
      });
    }
    return showModal({
      type: MODAL_TYPE.BASIC,
      props: {
        body: (
          <NonF2fDiagnosisAppInstallQrCodeBottomSheet
            appInstallLink={APP_INSTALL_LINK_FROM_HOSPITAL_SEARCH}
            onClickOverlay={closeModal}
          />
        ),
      },
    });
  };

  const getSearchEntireFilterBottomSheetProps = (): EntireBottomSheetProps => {
    return {
      isEntireFilterBottomSheet: isSearchEntireFilterBottomSheet,
      toggleSearchEntireFilterBottomSheet,
      isDefaultHospitalNearMode,
      locationName: getHeaderFullAddress(),
      switchRegionFilter,
      hospitalCommonFilter,
      sortFilterList,
      selectedSortFilter,
      isChangedHospitalFilters,
      updateEntireFilter,
    };
  };

  useEffect(() => {
    setIsCujModalTrackingInfo({
      campaignName: 'web_hospitals',
      trackContent: 'web_mid_mdl_list',
      contentId: keywordHook.keyword,
    });
  }, []);

  useEffect(() => {
    window.scrollTo({
      top: 0,
    });
  }, [router.query]);

  return {
    ...keywordHook,
    ...searchHospitalHook,
    ...hospitalFiltersHook,
    ...locationHook,
    getSearchEntireFilterBottomSheetProps,
    filterCount,
    goToEventPage,
    clickNonF2fDoctorListBanner,
    matchedCategory,
  };
};

export default useHospitalsSearchViewModel;
